/**
 * Monthly Insurance Invoice Management Page
 * Complete workflow: Preview → Generate → Submit → Track Payments
 */

import React, { useState, useEffect } from 'react';
import {
  FileText,
  Plus,
  Download,
  Send,
  DollarSign,
  Eye,
  Calendar,
  Filter,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Trash2,
  Printer,
} from 'lucide-react';
import monthlyInvoiceService, {
  MonthlyInvoice,
  MonthlyInvoiceDetail,
} from '../services/monthlyInvoiceService';
import { assureurService } from '../services/api';
import type { Assureur } from '../types';
import GenerateInvoiceModal from '../components/invoices/GenerateInvoiceModal';
import InvoiceDetailModal from '../components/invoices/InvoiceDetailModal';
import SubmitInvoiceModal from '../components/invoices/SubmitInvoiceModal';
import RecordPaymentModal from '../components/invoices/RecordPaymentModal';

const MonthlyInvoiceManagement: React.FC = () => {
  const [invoices, setInvoices] = useState<MonthlyInvoice[]>([]);
  const [assureurs, setAssureurs] = useState<Assureur[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState<MonthlyInvoiceDetail | null>(null);

  // Modals
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    assureur_id: '',
    status: '',
    year: new Date().getFullYear().toString(),
    month: '',
    overdue: false,
  });

  useEffect(() => {
    loadData();
  }, [filters]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [invoicesData, assureursResponse] = await Promise.all([
        monthlyInvoiceService.listInvoices({
          assureur_id: filters.assureur_id ? parseInt(filters.assureur_id) : undefined,
          status: filters.status || undefined,
          year: filters.year ? parseInt(filters.year) : undefined,
          month: filters.month ? parseInt(filters.month) : undefined,
          overdue: filters.overdue || undefined,
        }),
        assureurService.getAll(),
      ]);

      const assureursData = assureursResponse.data.results || assureursResponse.data || [];

      setInvoices(invoicesData);
      setAssureurs(assureursData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetail = async (invoice: MonthlyInvoice) => {
    try {
      const detail = await monthlyInvoiceService.getInvoice(invoice.id);
      setSelectedInvoice(detail);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Error loading invoice detail:', error);
    }
  };

  const handleViewPDF = async (invoice: MonthlyInvoice) => {
    try {
      await monthlyInvoiceService.viewPDF(invoice.id);
    } catch (error) {
      console.error('Error viewing PDF:', error);
      alert('Erreur lors de l\'ouverture du PDF');
    }
  };

  const handleDownloadPDF = async (invoice: MonthlyInvoice) => {
    try {
      await monthlyInvoiceService.downloadPDF(invoice.id, invoice.numero);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Erreur lors du téléchargement du PDF');
    }
  };

  const handleSubmit = async (invoice: MonthlyInvoice) => {
    try {
      const detail = await monthlyInvoiceService.getInvoice(invoice.id);
      setSelectedInvoice(detail);
      setShowSubmitModal(true);
    } catch (error) {
      console.error('Error loading invoice:', error);
    }
  };

  const handleRecordPayment = async (invoice: MonthlyInvoice) => {
    try {
      const detail = await monthlyInvoiceService.getInvoice(invoice.id);
      setSelectedInvoice(detail);
      setShowPaymentModal(true);
    } catch (error) {
      console.error('Error loading invoice:', error);
    }
  };

  const handleDelete = async (invoice: MonthlyInvoice) => {
    if (!confirm(`Êtes-vous sûr de vouloir supprimer définitivement la facture ${invoice.numero} ?\n\nCette action est irréversible.`)) {
      return;
    }

    try {
      const response = await monthlyInvoiceService.cancelInvoice(invoice.id);
      alert(response.message || 'Facture supprimée avec succès');
      loadData();
    } catch (error: any) {
      console.error('Error deleting invoice:', error);
      alert(error.response?.data?.error || 'Erreur lors de la suppression de la facture');
    }
  };

  const getStatusBadge = (status: string, statusDisplay: string) => {
    const colors = {
      draft: 'son-chip-gray',
      generated: 'son-chip-blue',
      submitted: 'son-chip-purple',
      partially_paid: 'son-chip-amber',
      paid: 'son-chip-green',
      rejected: 'son-chip-red',
      cancelled: 'son-chip-gray',
    };

    const icons = {
      draft: Clock,
      generated: FileText,
      submitted: Send,
      partially_paid: DollarSign,
      paid: CheckCircle,
      rejected: XCircle,
      cancelled: XCircle,
    };

    const Icon = icons[status as keyof typeof icons] || Clock;
    const colorClass = colors[status as keyof typeof colors] || colors.draft;

    return (
      <span className={`son-chip ${colorClass}`}>
        <Icon className="w-3 h-3" aria-hidden />
        {statusDisplay}
      </span>
    );
  };

  const getPaymentProgress = (invoice: MonthlyInvoice) => {
    // Convert to number safely
    const percentage = typeof invoice.payment_percentage === 'number'
      ? invoice.payment_percentage
      : parseFloat(invoice.payment_percentage || '0');

    const color = percentage === 0 ? 'bg-gray-300' : percentage < 100 ? 'bg-amber-500' : 'bg-[color:var(--teal)]';

    return (
      <div className="w-full bg-[color:var(--border-soft)] rounded-full h-1.5 overflow-hidden" role="presentation">
        <div
          className={`h-1.5 rounded-full ${color}`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>
    );
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-5 son-page-head">
        <div>
          <h2 className="son-h1 !text-[19px]">Factures Mensuelles</h2>
          <p className="son-sub">Génération et suivi des factures mensuelles consolidées</p>
        </div>
        <button
          onClick={() => setShowGenerateModal(true)}
          className="btn-primary"
        >
          <Plus className="w-4 h-4" aria-hidden />
          Générer Facture
        </button>
      </div>

      {/* Filters */}
      <div className="son-toolbar mb-5">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-[color:var(--muted)]" aria-hidden />
          <h3 className="son-field-label !mb-0">Filtres</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <select
            aria-label="Filtrer par compagnie d'assurance"
            value={filters.assureur_id}
            onChange={(e) => setFilters({ ...filters, assureur_id: e.target.value })}
            className="form-select text-gray-700"
          >
            <option value="">Toutes les compagnies</option>
            {assureurs.map((a) => (
              <option key={a.id} value={a.id}>
                {a.nom}
              </option>
            ))}
          </select>

          <select
            aria-label="Filtrer par statut"
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="form-select text-gray-700"
          >
            <option value="">Tous les statuts</option>
            <option value="draft">Brouillon</option>
            <option value="generated">Généré</option>
            <option value="submitted">Soumis</option>
            <option value="partially_paid">Partiellement Payé</option>
            <option value="paid">Payé</option>
            <option value="rejected">Rejeté</option>
          </select>

          <select
            aria-label="Filtrer par annee"
            value={filters.year}
            onChange={(e) => setFilters({ ...filters, year: e.target.value })}
            className="form-select text-gray-700"
          >
            {[2024, 2025, 2026].map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>

          <select
            aria-label="Filtrer par mois"
            value={filters.month}
            onChange={(e) => setFilters({ ...filters, month: e.target.value })}
            className="form-select text-gray-700"
          >
            <option value="">Tous les mois</option>
            {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
              <option key={month} value={month}>
                {monthlyInvoiceService.getMonthName(month)}
              </option>
            ))}
          </select>

          <label className="flex items-center gap-2 border border-gray-200 rounded-xl px-3 py-2.5 bg-white cursor-pointer hover:border-gray-300">
            <input
              type="checkbox"
              checked={filters.overdue}
              onChange={(e) => setFilters({ ...filters, overdue: e.target.checked })}
              className="w-4 h-4 rounded text-teal-600 focus:ring-teal-500"
            />
            <span className="text-sm text-gray-700">En retard</span>
          </label>
        </div>
      </div>

      {/* Invoices Table */}
      <div className="son-table-wrap">
        {loading ? (
          <div className="p-4 space-y-3" aria-busy="true">
            <span className="sr-only">Chargement...</span>
            {Array.from({ length: 4 }).map((_, i) => <div key={i} className="son-skeleton h-10" />)}
          </div>
        ) : invoices.length === 0 ? (
          <div className="son-empty">
            <div className="son-empty-icon"><FileText className="h-6 w-6" aria-hidden /></div>
            <p className="font-semibold text-[color:var(--text)]">Aucune facture trouvée</p>
            <p>Générez votre première facture mensuelle.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="son-table">
              <thead>
                <tr>
                  <th>
                    Numéro
                  </th>
                  <th>
                    Compagnie
                  </th>
                  <th>
                    Période
                  </th>
                  <th>
                    Statut
                  </th>
                  <th>
                    Net à Payer
                  </th>
                  <th>
                    Paiement
                  </th>
                  <th className="!text-right">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => (
                  <tr key={invoice.id}>
                    <td className="whitespace-nowrap">
                      <button
                        onClick={() => handleViewDetail(invoice)}
                        className="font-bold tabular-nums text-[color:var(--blue)] hover:underline"
                      >
                        {invoice.numero}
                      </button>
                    </td>
                    <td className="whitespace-nowrap">
                      <div className="font-semibold">{invoice.assureur_name}</div>
                      <div className="text-[12px] text-[color:var(--muted)]">
                        {invoice.number_of_patients} patients · {invoice.number_of_invoices} factures
                      </div>
                    </td>
                    <td className="whitespace-nowrap">
                      <div className="flex items-center gap-1.5 capitalize">
                        <Calendar className="w-4 h-4 text-[color:var(--muted)]" aria-hidden />
                        {monthlyInvoiceService.getMonthName(invoice.month)} {invoice.year}
                      </div>
                    </td>
                    <td className="whitespace-nowrap">
                      {getStatusBadge(invoice.status, invoice.status_display)}
                      {invoice.is_overdue && (
                        <div className="flex items-center text-xs font-semibold text-[#C0392B] mt-1">
                          <AlertCircle className="w-3 h-3 mr-1" />
                          {invoice.days_overdue} jours de retard
                        </div>
                      )}
                    </td>
                    <td className="whitespace-nowrap">
                      <div className="font-bold tabular-nums">
                        {monthlyInvoiceService.formatCurrency(invoice.net_to_pay)}
                      </div>
                      <div className="text-[11.5px] text-[color:var(--muted)] tabular-nums">
                        -2% · {monthlyInvoiceService.formatCurrency(invoice.remise_amount)}
                      </div>
                    </td>
                    <td className="min-w-[160px]">
                      <div className="text-[11.5px] text-[color:var(--muted)] mb-1 tabular-nums">
                        {monthlyInvoiceService.formatCurrency(invoice.amount_paid)} /{' '}
                        {monthlyInvoiceService.formatCurrency(invoice.net_to_pay)}
                      </div>
                      {getPaymentProgress(invoice)}
                      <div className="text-[11.5px] font-semibold text-[color:var(--muted)] mt-1 tabular-nums">
                        {(typeof invoice.payment_percentage === 'number'
                          ? invoice.payment_percentage
                          : parseFloat(invoice.payment_percentage || '0')
                        ).toFixed(0)}%
                      </div>
                    </td>
                    <td className="whitespace-nowrap">
                      <div className="flex items-center justify-end gap-0.5">
                        <button
                          onClick={() => handleViewDetail(invoice)}
                          className="son-icon-btn is-blue"
                          title="Voir détails"
                          aria-label="Voir détails"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {invoice.pdf_file && (
                          <>
                            <button
                              onClick={() => handleViewPDF(invoice)}
                              className="son-icon-btn is-purple"
                              title="Ouvrir et imprimer PDF"
                              aria-label="Ouvrir et imprimer PDF"
                            >
                              <Printer className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDownloadPDF(invoice)}
                              className="son-icon-btn is-teal"
                              title="Télécharger PDF"
                              aria-label="Télécharger PDF"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        {(invoice.status === 'draft' || invoice.status === 'generated') && (
                          <button
                            onClick={() => handleSubmit(invoice)}
                            className="son-icon-btn is-amber"
                            title="Soumettre"
                            aria-label="Soumettre"
                          >
                            <Send className="w-4 h-4" />
                          </button>
                        )}
                        {(invoice.status === 'submitted' || invoice.status === 'partially_paid') && (
                          <button
                            onClick={() => handleRecordPayment(invoice)}
                            className="son-icon-btn is-purple"
                            title="Enregistrer paiement"
                            aria-label="Enregistrer paiement"
                          >
                            <DollarSign className="w-4 h-4" />
                          </button>
                        )}
                        {invoice.status === 'draft' && (
                          <button
                            onClick={() => handleDelete(invoice)}
                            className="son-icon-btn is-red"
                            title="Supprimer"
                            aria-label="Supprimer"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals */}
      {showGenerateModal && (
        <GenerateInvoiceModal
          assureurs={assureurs}
          onClose={() => setShowGenerateModal(false)}
          onGenerated={() => {
            setShowGenerateModal(false);
            loadData();
          }}
        />
      )}

      {showDetailModal && selectedInvoice && (
        <InvoiceDetailModal
          invoice={selectedInvoice}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedInvoice(null);
          }}
          onUpdate={loadData}
        />
      )}

      {showSubmitModal && selectedInvoice && (
        <SubmitInvoiceModal
          invoice={selectedInvoice}
          onClose={() => {
            setShowSubmitModal(false);
            setSelectedInvoice(null);
          }}
          onSubmitted={() => {
            setShowSubmitModal(false);
            setSelectedInvoice(null);
            loadData();
          }}
        />
      )}

      {showPaymentModal && selectedInvoice && (
        <RecordPaymentModal
          invoice={selectedInvoice}
          onClose={() => {
            setShowPaymentModal(false);
            setSelectedInvoice(null);
          }}
          onPaymentRecorded={() => {
            setShowPaymentModal(false);
            setSelectedInvoice(null);
            loadData();
          }}
        />
      )}
    </div>
  );
};

export default MonthlyInvoiceManagement;
